import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";

const features = [
  {
    icon: "ri-bug-line",
    color: "#ff4444",
    bg: "rgba(255,68,68,0.1)",
    title: "Failure Patterns",
    desc: "Learn the most common production failure modes — cascading failures, config drift, alert fatigue — through real scenarios that mirror actual outages.",
  },
  {
    icon: "ri-alarm-warning-line",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.1)",
    title: "Incident Response",
    desc: "Practice structured incident response: triage, communication, escalation, and resolution — all under simulated pressure without real consequences.",
  },
  {
    icon: "ri-eye-line",
    color: "#00d9ff",
    bg: "rgba(0,217,255,0.1)",
    title: "Observability Thinking",
    desc: "Build intuition for metrics, logs, and traces. Learn when to use each signal and how to correlate them to find root cause fast.",
  },
  {
    icon: "ri-file-list-3-line",
    color: "#00ff88",
    bg: "rgba(0,255,136,0.1)",
    title: "Postmortem Lessons",
    desc: "Internalize blameless postmortem culture. Understand how systemic thinking and action items prevent recurrence better than blame ever could.",
  },
];

const howItWorks = [
  {
    icon: "ri-arrow-down-line",
    color: "#ff4444",
    bg: "rgba(255,68,68,0.12)",
    label: "Snakes",
    title: "Failure Patterns",
    desc: "Land on a snake square and slide down. Each snake represents a real production failure — alert fatigue, missing runbooks, untested backups, config drift.",
  },
  {
    icon: "ri-arrow-up-line",
    color: "#00ff88",
    bg: "rgba(0,255,136,0.12)",
    label: "Ladders",
    title: "Reliability Wins",
    desc: "Land on a ladder and climb up. Each ladder represents a reliability best practice — SLO-driven alerts, canary deployments, chaos engineering, blameless postmortems.",
  },
  {
    icon: "ri-file-warning-line",
    color: "#00d9ff",
    bg: "rgba(0,217,255,0.12)",
    label: "Incident Cards",
    title: "Real Debugging Questions",
    desc: "Land on a card square and face a real engineering scenario. Answer correctly to move forward. Answer wrong and fall back. Learn from every decision.",
  },
];

const ranks = [
  { rank: "Incident Intern", color: "#ff4444", icon: "ri-user-line" },
  { rank: "Page Receiver", color: "#f59e0b", icon: "ri-notification-3-line" },
  { rank: "On-Call Survivor", color: "#f59e0b", icon: "ri-alarm-warning-line" },
  { rank: "Reliability Engineer", color: "#00d9ff", icon: "ri-settings-3-line" },
  { rank: "Incident Commander", color: "#00d9ff", icon: "ri-command-line" },
  { rank: "Production Guardian", color: "#00ff88", icon: "ri-shield-star-line" },
];

export default function Home() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-mono overflow-x-hidden">
      <Navbar />

      {/* Grid background */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,255,136,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,136,0.03) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Hero */}
      <section className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 pt-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00ff88]/10 border border-[#00ff88]/20 mb-8">
            <span className="w-2 h-2 rounded-full bg-[#00ff88] animate-pulse"></span>
            <span className="text-[#00ff88] text-xs font-bold tracking-widest uppercase">For SREs &amp; DevOps Engineers</span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black leading-tight text-white mb-6">
            A game that teaches engineers{" "}
            <span className="text-[#00ff88]">how production</span>{" "}
            <span className="relative">
              systems fail
              <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#ff4444]/60"></span>
            </span>
          </h1>

          <p className="text-lg md:text-xl text-white/50 max-w-2xl mx-auto mb-10 leading-relaxed">
            Climb with good engineering decisions. Fall with real outage mistakes.
            Built for SREs, DevOps, and reliability-minded teams.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/game"
              className="flex items-center gap-2 px-8 py-4 bg-[#00ff88] text-[#0a0a0a] font-black text-base rounded-md hover:bg-[#00e07a] transition-all hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-gamepad-line"></i>
              Play Now
              <i className="ri-arrow-right-line"></i>
            </Link>
            <a
              href="https://www.skool.com/incident-engineering-academy-3597"
              target="_blank"
              rel="noopener noreferrer"
              title="Join the Incident Engineering Academy on Skool"
              className="flex items-center gap-2 px-8 py-4 border border-[#00d9ff]/40 text-[#00d9ff] font-bold text-base rounded-md hover:bg-[#00d9ff]/10 transition-all whitespace-nowrap"
            >
              <i className="ri-community-line"></i>
              Join the Community
            </a>
          </div>

          {/* Stats bar */}
          <div className="mt-16 grid grid-cols-3 gap-4 max-w-lg mx-auto">
            {[
              { val: "100", label: "Board Squares" },
              { val: "12+", label: "Incident Cards" },
              { val: "6", label: "Engineer Ranks" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-2xl font-black text-[#00ff88]">{s.val}</div>
                <div className="text-xs text-white/40 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/20 animate-bounce">
          <span className="text-xs tracking-widest">SCROLL</span>
          <i className="ri-arrow-down-line"></i>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative z-10 py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#00d9ff] text-xs font-bold tracking-widest uppercase">Game Mechanics</span>
            <h2 className="text-3xl md:text-5xl font-black text-white mt-3">How It Works</h2>
            <p className="text-white/40 mt-4 max-w-xl mx-auto">
              Three types of squares. Each one teaches you something real about production systems.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {howItWorks.map((item) => (
              <div
                key={item.label}
                className="relative p-6 rounded-xl border border-white/8 bg-[#111] hover:border-white/15 transition-all group"
              >
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-lg mb-5"
                  style={{ background: item.bg }}
                >
                  <i className={`${item.icon} text-xl`} style={{ color: item.color }}></i>
                </div>
                <div
                  className="inline-block px-2 py-0.5 rounded text-xs font-bold mb-3"
                  style={{ background: item.bg, color: item.color }}
                >
                  {item.label}
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{item.title}</h3>
                <p className="text-sm text-white/40 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 py-24 px-4 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#00d9ff] text-xs font-bold tracking-widest uppercase">What You'll Learn</span>
            <h2 className="text-3xl md:text-5xl font-black text-white mt-3">Master Production Reliability</h2>
            <p className="text-white/40 mt-4 max-w-xl mx-auto">
              Every square, card, and scenario is grounded in real-world SRE and DevOps practice.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f) => (
              <div
                key={f.title}
                className="p-6 rounded-xl border border-white/8 bg-[#111] hover:border-white/15 transition-all group cursor-default"
              >
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-lg mb-5"
                  style={{ background: f.bg }}
                >
                  <i className={`${f.icon} text-xl`} style={{ color: f.color }}></i>
                </div>
                <h3 className="text-base font-bold text-white mb-3">{f.title}</h3>
                <p className="text-sm text-white/40 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ranks */}
      <section className="relative z-10 py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#00d9ff] text-xs font-bold tracking-widest uppercase">Progression System</span>
            <h2 className="text-3xl md:text-5xl font-black text-white mt-3">Earn Your Rank</h2>
            <p className="text-white/40 mt-4 max-w-xl mx-auto">
              How far you get on the board determines your engineering rank. Can you reach Production Guardian?
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {ranks.map((r, i) => (
              <div
                key={r.rank}
                className="flex flex-col items-center p-4 rounded-xl border border-white/8 bg-[#111] text-center"
              >
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-full mb-3 border-2"
                  style={{ borderColor: r.color, background: `${r.color}15` }}
                >
                  <i className={`${r.icon} text-lg`} style={{ color: r.color }}></i>
                </div>
                <div className="text-xs text-white/30 mb-1">Rank {i + 1}</div>
                <div className="text-xs font-bold text-white leading-tight">{r.rank}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="community" className="relative z-10 py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div
            className="relative rounded-2xl overflow-hidden p-10 md:p-16 text-center border border-[#00ff88]/20"
            style={{
              background: "linear-gradient(135deg, rgba(0,255,136,0.06) 0%, rgba(0,217,255,0.06) 100%)",
            }}
          >
            <div className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: "linear-gradient(rgba(0,255,136,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,136,0.04) 1px, transparent 1px)",
                backgroundSize: "30px 30px",
              }}
            />
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00ff88]/10 border border-[#00ff88]/20 mb-6">
                <span className="w-2 h-2 rounded-full bg-[#00ff88] animate-pulse"></span>
                <span className="text-[#00ff88] text-xs font-bold tracking-widest uppercase">Community</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4">
                Ready to level up your<br />incident response skills?
              </h2>
              <p className="text-white/40 text-lg mb-10 max-w-xl mx-auto">
                Join engineers learning production reliability through real-world scenarios and shared postmortems.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/game"
                  className="flex items-center justify-center gap-2 px-8 py-4 bg-[#00ff88] text-[#0a0a0a] font-black text-base rounded-md hover:bg-[#00e07a] transition-all whitespace-nowrap"
                >
                  <i className="ri-gamepad-line"></i>
                  Start Playing Now
                </Link>
                <a
                  href="https://www.skool.com/incident-engineering-academy-3597"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Join the Incident Engineering Academy on Skool"
                  className="flex items-center justify-center gap-2 px-8 py-4 border border-[#00d9ff]/40 text-[#00d9ff] font-bold text-base rounded-md hover:bg-[#00d9ff]/10 transition-all whitespace-nowrap"
                >
                  <i className="ri-community-line"></i>
                  Join Skool Community
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="https://static.readdy.ai/image/7a396de9d4a51ca3342f9bdb460bfa21/efd74d9ca1c0839b36715e646624f840.png"
              alt="Incident Ladder"
              className="h-6 w-auto"
            />
            <span className="text-white/30 text-sm font-mono">IncidentLadder</span>
          </div>
          <p className="text-white/20 text-xs font-mono">
            Built for SREs, DevOps engineers, and reliability-minded teams.
          </p>
        </div>
      </footer>
    </div>
  );
}
